import {t, useLanguage} from './i18n';
// src/Register.jsx
// LOOP AUTH OPTIMAL R31 — social-first registration with progressive email form.

import React, { useMemo, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  signOut,
  getAuth,
} from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "./firebase";
import { isIosCapacitor, httpRequestJson } from "./utils/nativeHttp";
import { clearLoopAuthState } from "./utils/manualAuth";
import {
  LOOP_PHONE_COUNTRIES,
  cleanPhoneNationalInput,
  phoneSnapshotFromParts,
} from "./utils/phoneUtils";
import {
  LOOP_LEGAL_TERMS_VERSION,
  LOOP_LEGAL_PRIVACY_VERSION,
  LOOP_LEGAL_PAYMENTS_VERSION,
  LOOP_LEGAL_SNAPSHOT_VERSION,
} from "./utils/legalVersions";
import {
  getLoopAuthPlatform,
  isAppleAuthAvailable,
  signInWithAppleAcrossPlatforms,
  signInWithGoogleAcrossPlatforms,
  socialAuthErrorMessage,
} from "./auth/socialAuth";

const BRAND = "#00675f";
const BRAND_2 = "#00897f";

const COUNTRY_CODES = LOOP_PHONE_COUNTRIES.map((item) => ({
  code: item.dialCode,
  country: item.countryCode,
  label: item.label,
  placeholder: item.placeholder,
}));

const LOOP_AUTH_FUNCTIONS_BASE =
  "https://us-central1-web-app-d9799.cloudfunctions.net";
const LOOP_REGISTER_HTTP_URL = `${LOOP_AUTH_FUNCTIONS_BASE}/registerEmailUserHttpV28`;

function withTimeout(promise, ms, label = "timeout") {
  let timer = null;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(label)), ms);
  });
  return Promise.race([
    Promise.resolve(promise).finally(() => {
      try {
        if (timer) clearTimeout(timer);
      } catch (_) {}
    }),
    timeout,
  ]);
}

function friendlyRegisterError(err) {
  const raw = String(err?.code || err?.message || err || "").toLowerCase();
  if (raw.includes("auth/email-already-in-use")) {
    return "Este correo ya está registrado. Inicia sesión o usa otro correo.";
  }
  if (raw.includes("auth/invalid-email")) {
    return "El correo no tiene un formato válido.";
  }
  if (raw.includes("auth/weak-password")) {
    return "Tu contraseña debe tener al menos 6 caracteres.";
  }
  if (raw.includes("auth/network-request-failed") || raw.includes("network")) {
    return "No pudimos conectarnos. Revisa tu internet e intenta nuevamente.";
  }
  if (raw.includes("auth/too-many-requests")) {
    return "Hiciste varios intentos seguidos. Espera unos minutos e intenta nuevamente.";
  }
  if (raw.includes("permission-denied")) {
    return "No pudimos completar el registro por permisos. Intenta nuevamente o contacta soporte.";
  }
  return "No pudimos crear tu cuenta. Revisa los datos e intenta nuevamente.";
}

function registerHttpErrorMessage(err) {
  const raw = String(err?.message || err?.code || err || "").toLowerCase();
  if (
    raw.includes("email-already-in-use") ||
    raw.includes("already-exists") ||
    raw.includes("409")
  ) {
    return "Este correo ya está registrado. Inicia sesión o usa otro correo.";
  }
  if (raw.includes("weak-password")) {
    return "Tu contraseña debe tener al menos 6 caracteres.";
  }
  if (raw.includes("invalid-email")) {
    return "El correo no tiene un formato válido.";
  }
  if (raw.includes("too-many") || raw.includes("429")) {
    return "Hiciste varios intentos seguidos. Espera unos minutos e intenta nuevamente.";
  }
  if (raw.includes("timeout")) {
    return "El registro tardó demasiado. Revisa tu conexión e intenta nuevamente.";
  }
  return friendlyRegisterError(err);
}

function isAdultEnough(dateValue) {
  if (!dateValue) return false;
  const birth = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(birth.getTime())) return false;
  const today = new Date();
  const min = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate(),
  );
  return birth <= min;
}

function cleanPhone(value, countryCode = "+507") {
  return cleanPhoneNationalInput(value, countryCode);
}

function getRegisterNextTarget(location) {
  try {
    const params = new URLSearchParams(location?.search || "");
    const fromSearch = params.get("next") || params.get("redirect") || "";
    const fromState =
      typeof location?.state?.from === "string" ? location.state.from : "";
    const target = fromSearch || fromState || "";
    if (!target || !target.startsWith("/") || target.startsWith("//"))
      return "";
    return target;
  } catch (_) {
    return "";
  }
}

function completeProfileRoute(nextTarget = "/") {
  const target = nextTarget && nextTarget.startsWith("/") ? nextTarget : "/";
  return `/complete-profile?next=${encodeURIComponent(target)}`;
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 19.5-8.9 19.5-20c0-1.2-.1-2.3-.3-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 10-2 13.6-5.3l-6.3-5.3C29.2 35 26.7 36 24 36c-5.2 0-9.6-3.3-11.2-7.9l-6.5 5C9.6 39.5 16.3 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.1-4.1 5.4l6.3 5.3C37.1 39.1 44 34 44 24c0-1.2-.1-2.3-.4-3.5z"
      />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M16.7 13.1c0-2.5 2.1-3.7 2.2-3.8-1.2-1.7-3-1.9-3.6-2-1.5-.2-3 .9-3.8.9s-2-.9-3.3-.8c-1.7 0-3.2 1-4.1 2.5-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.2 2.5 1.3-.1 1.8-.8 3.3-.8s2 .8 3.3.8c1.4 0 2.3-1.2 3.1-2.5 1-1.4 1.4-2.8 1.4-2.9-.1-.1-2.9-1.2-3-4.1z" />
      <path d="M14.3 5.7c.7-.9 1.2-2.1 1.1-3.3-1.1 0-2.3.7-3.1 1.6-.7.8-1.3 2-1.1 3.2 1.2.1 2.4-.6 3.1-1.5z" />
    </svg>
  );
}

export default function Register() {
  useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const nextTarget = getRegisterNextTarget(location);
  const platform = getLoopAuthPlatform();
  const showApple = isAppleAuthAvailable();

  const [emailMode, setEmailMode] = useState(false);
  const [nombre, setNombre] = useState("");
  const [apellido, setApellido] = useState("");
  const [email, setEmail] = useState("");
  const [countryCode, setCountryCode] = useState("+507");
  const [telefono, setTelefono] = useState("");
  const [fechaNacimiento, setFechaNacimiento] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [loadingApple, setLoadingApple] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const selectedCountry = useMemo(
    () =>
      COUNTRY_CODES.find((item) => item.code === countryCode) ||
      COUNTRY_CODES[0],
    [countryCode],
  );

  const passwordStrength = useMemo(() => {
    const p = String(password || "");
    let score = 0;
    if (p.length >= 6) score += 1;
    if (p.length >= 10) score += 1;
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) score += 1;
    if (/\d/.test(p)) score += 1;
    if (/[^A-Za-z0-9]/.test(p)) score += 1;
    if (!p) return { label: "", score: 0 };
    if (score <= 1) return { label: t("Básica"), score };
    if (score <= 3) return { label: t("Buena"), score };
    return { label: t("Fuerte"), score };
  }, [password]);

  const busy = loading || loadingGoogle || loadingApple;

  const goAfterSocialAuth = (result) => {
    const finalTarget = nextTarget || "/";
    if (result?.profileComplete) {
      navigate(finalTarget, { replace: true });
      return;
    }
    try {
      window.localStorage.setItem("loop_post_auth_redirect", finalTarget);
      window.sessionStorage.setItem("loop_post_auth_redirect", finalTarget);
    } catch (_) {}
    navigate(completeProfileRoute(finalTarget), { replace: true });
  };

  const handleGoogle = async () => {
    setError("");
    setLoadingGoogle(true);
    try {
      const result = await signInWithGoogleAcrossPlatforms();
      goAfterSocialAuth(result);
    } catch (err) {
      console.error("[Register R31] Google failed", err);
      setError(socialAuthErrorMessage(err));
    } finally {
      setLoadingGoogle(false);
    }
  };

  const handleApple = async () => {
    setError("");
    setLoadingApple(true);
    try {
      const result = await signInWithAppleAcrossPlatforms();
      goAfterSocialAuth(result);
    } catch (err) {
      console.error("[Register R31] Apple failed", err);
      setError(socialAuthErrorMessage(err));
    } finally {
      setLoadingApple(false);
    }
  };

  const validate = () => {
    const next = {};
    const emailTrim = String(email || "")
      .trim()
      .toLowerCase();
    const phoneLocal = cleanPhone(telefono, countryCode);

    if (!nombre.trim()) next.nombre = "Ingresa tu nombre.";
    if (!apellido.trim()) next.apellido = "Ingresa tu apellido.";
    if (!emailTrim) next.email = "Ingresa tu correo.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
      next.email = "Correo inválido.";
    }
    if (!phoneLocal) next.telefono = "Ingresa tu teléfono.";
    if (!fechaNacimiento) {
      next.fechaNacimiento = "Ingresa tu fecha de nacimiento.";
    } else if (!isAdultEnough(fechaNacimiento)) {
      next.fechaNacimiento = "Debes ser mayor de edad para crear una cuenta.";
    }
    if (!password) next.password = t("Crea una contraseña.");
    else if (password.length < 6) next.password = "Mínimo 6 caracteres.";
    if (!confirm) next.confirm = t("Confirma tu contraseña.");
    else if (password !== confirm)
      next.confirm = t("Las contraseñas no coinciden.");
    if (!acceptedTerms) {
      next.acceptedTerms =
        "Debes aceptar términos, privacidad y condiciones de pagos.";
    }

    setFieldErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleRegister = async (event) => {
    event?.preventDefault?.();
    setError("");
    setFieldErrors({});
    if (!validate()) return;

    setLoading(true);
    const emailTrim = String(email || "")
      .trim()
      .toLowerCase();
    const phoneLocal = cleanPhone(telefono, countryCode);
    const phoneSnapshot = phoneSnapshotFromParts(countryCode, phoneLocal);
    const firstName = nombre.trim();
    const lastName = apellido.trim();

    try {
      const pendingProfile = {
        nombre: firstName,
        firstName,
        apellido: lastName,
        lastName,
        fullName: `${firstName} ${lastName}`.trim(),
        email: emailTrim,
        telefono: phoneSnapshot.phoneDisplay,
        phone: phoneSnapshot.phoneDisplay,
        phoneCountryCode: phoneSnapshot.phoneCountryCode,
        phoneDialCode: phoneSnapshot.phoneDialCode,
        phoneLocal,
        phoneNational: phoneSnapshot.phoneNational,
        phoneDisplay: phoneSnapshot.phoneDisplay,
        phoneE164: phoneSnapshot.phoneE164,
        phoneNormalized: phoneSnapshot.phoneE164,
        phoneVerified: false,
        phoneVerificationProvider: null,
        fechaNacimiento,
        birthDate: fechaNacimiento,
        acceptedTerms: true,
        termsAccepted: true,
        privacyAccepted: true,
        paymentsPolicyAccepted: true,
        legalAccepted: true,
        legalAcceptedFrom: "signup",
        legalAcceptedSource: "register_form",
        legalTermsVersion: LOOP_LEGAL_TERMS_VERSION,
        legalPrivacyVersion: LOOP_LEGAL_PRIVACY_VERSION,
        legalPaymentsVersion: LOOP_LEGAL_PAYMENTS_VERSION,
        legalSnapshotVersion: LOOP_LEGAL_SNAPSHOT_VERSION,
        legalAcceptedPlatform: platform,
        legalAcceptedUserAgent:
          typeof navigator !== "undefined"
            ? String(navigator.userAgent || "").slice(0, 500)
            : "",
        minimumAgeConfirmed: true,
        profileCompleted: true,
        onboardingVersion: 2,
      };

      const goToCode = ({ uid, warning = "" }) => {
        clearLoopAuthState({ keepPendingRegistration: true });
        window.localStorage.setItem("pending_uid", uid);
        window.localStorage.setItem("pending_email", emailTrim);
        if (nextTarget) {
          window.localStorage.setItem("loop_post_auth_redirect", nextTarget);
          window.sessionStorage.setItem("loop_post_auth_redirect", nextTarget);
        }
        navigate("/enter-code", {
          replace: true,
          state: {
            uid,
            email: emailTrim,
            next: nextTarget || undefined,
            warning: warning || undefined,
          },
        });
      };

      if (isIosCapacitor()) {
        const result = await withTimeout(
          httpRequestJson({
            url: LOOP_REGISTER_HTTP_URL,
            method: "POST",
            data: {
              ...pendingProfile,
              password,
              source: "ios_capacitor_register_r30",
            },
          }),
          35000,
          "ios-register-timeout",
        );

        if (!result?.uid) throw new Error("Registro iOS completó sin uid.");
        goToCode({
          uid: String(result.uid),
          warning:
            result.codeSent === false
              ? "Creamos tu cuenta, pero no pudimos confirmar el envío del código. Usa reenviar código o contacta soporte."
              : "",
        });
        return;
      }

      const auth = getAuth();
      const cred = await withTimeout(
        createUserWithEmailAndPassword(auth, emailTrim, password),
        30000,
        "web-register-auth-timeout",
      );
      const user = cred.user;

      try {
        await withTimeout(
          setDoc(doc(db, "pendingRegistrations", user.uid), {
            ...pendingProfile,
            acceptedTermsAt: serverTimestamp(),
            termsAcceptedAt: serverTimestamp(),
            privacyAcceptedAt: serverTimestamp(),
            paymentsPolicyAcceptedAt: serverTimestamp(),
            legalAcceptedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          }),
          25000,
          "web-register-pending-timeout",
        );
      } catch (pendingError) {
        console.error(
          "[Register R31] pendingRegistration failed",
          pendingError,
        );
        try {
          await user?.delete?.();
        } catch (_) {}
        setError(t("No pudimos completar el registro. Intenta nuevamente."));
        return;
      }

      let warning = "";
      try {
        const functions = getFunctions();
        const sendFn = httpsCallable(functions, "sendVerificationCode");
        const sendRes = await withTimeout(
          sendFn({ uid: user.uid, email: emailTrim }),
          30000,
          "verification-code-timeout",
        );
        if (sendRes?.data?.codeSent === false) {
          warning =
            "Creamos tu cuenta, pero no pudimos confirmar el envío del código. Usa reenviar código o contacta soporte.";
        }
      } catch (sendError) {
        console.warn("[Register R31] verification send failed", sendError);
        warning =
          "Creamos tu cuenta, pero no pudimos confirmar el envío del código. Usa reenviar código o contacta soporte.";
      }

      try {
        await signOut(auth);
      } catch (_) {}
      goToCode({ uid: user.uid, warning });
    } catch (err) {
      console.error("[Register R31] register error", err);
      setError(
        isIosCapacitor()
          ? registerHttpErrorMessage(err)
          : friendlyRegisterError(err),
      );
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (key) =>
    `register-pro-input ${fieldErrors[key] ? "is-error" : ""}`;

  return (
    <main className="register-pro-page">
      <style>{styles}</style>
      <section className="register-pro-card">
        <div className="register-pro-top">
          <div className="register-pro-logo">
            <img src="/logo3.png" alt="Loop" draggable="false" />
          </div>
          <div>
            <div className="register-pro-eyebrow">{t("Nuevo en Loop")}</div>
            <h1>{t("Crear cuenta")}</h1>
            <p>{t("Empieza rápido y completa solo lo necesario.")}</p>
          </div>
        </div>

        {error ? (
          <div className="register-pro-alert" role="alert">
            <span>{t(error)}</span>
            {error.includes("Ya existe") ||
            error.includes("ya está registrado") ? (
              <button
                type="button"
                onClick={() =>
                  navigate(
                    nextTarget
                      ? `/login?from=${encodeURIComponent(nextTarget)}`
                      : "/login",
                    { state: { from: nextTarget || "/" } },
                  )
                }
              >{t("Iniciar sesión")}</button>
            ) : null}
          </div>
        ) : null}

        {!emailMode ? (
          <div className="register-fast-options">
            <button
              type="button"
              className="register-social google"
              onClick={handleGoogle}
              disabled={busy}
            >
              <GoogleIcon />
              <span>
                {loadingGoogle ? t("Conectando...") : t("Continuar con Google")}
              </span>
            </button>

            {showApple ? (
              <button
                type="button"
                className="register-social apple"
                onClick={handleApple}
                disabled={busy}
              >
                <AppleIcon />
                <span>
                  {loadingApple ? t("Conectando...") : t("Continuar con Apple")}
                </span>
              </button>
            ) : null}

            <div className="register-divider">
              <span>o</span>
            </div>

            <button
              type="button"
              className="register-email-choice"
              onClick={() => {
                setError("");
                setEmailMode(true);
              }}
              disabled={busy}
            >{t("Continuar con correo")}</button>

            <p className="register-social-note">
              Google y Apple crean o abren tu cuenta automáticamente. Solo te
              pediremos los datos que falten.
            </p>

            <p className="register-legal-note">{t("Para terminar tu perfil confirmarás tu edad y aceptarás los")}{" "}
              <Link to="/terms" target="_blank" rel="noreferrer">{t("Términos")}</Link>
              ,{" "}
              <Link to="/privacy" target="_blank" rel="noreferrer">{t("Privacidad")}</Link>{" "}
              y{" "}
              <Link to="/payments" target="_blank" rel="noreferrer">{t("Pagos")}</Link>
              .
            </p>
          </div>
        ) : (
          <form onSubmit={handleRegister} className="register-pro-form">
            <button
              type="button"
              className="register-back-options"
              onClick={() => {
                setError("");
                setFieldErrors({});
                setEmailMode(false);
              }}
              disabled={busy}
            >{t("← Volver a Google y Apple")}</button>

            <div className="register-pro-grid">
              <label>
                <span>{t("Nombre")}</span>
                <input
                  className={inputClass("nombre")}
                  type="text"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Juan"
                  autoComplete="given-name"
                />
                {fieldErrors.nombre ? (
                  <small>{t(fieldErrors.nombre)}</small>
                ) : null}
              </label>
              <label>
                <span>{t("Apellido")}</span>
                <input
                  className={inputClass("apellido")}
                  type="text"
                  value={apellido}
                  onChange={(e) => setApellido(e.target.value)}
                  placeholder="Quirós"
                  autoComplete="family-name"
                />
                {fieldErrors.apellido ? (
                  <small>{t(fieldErrors.apellido)}</small>
                ) : null}
              </label>
              <label className="wide">
                <span>{t("Correo")}</span>
                <input
                  className={inputClass("email")}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="correo@ejemplo.com"
                  autoComplete="email"
                />
                {fieldErrors.email ? <small>{t(fieldErrors.email)}</small> : null}
              </label>
              <label>
                <span>{t("País")}</span>
                <select
                  className="register-pro-input"
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                >
                  {COUNTRY_CODES.map((item) => (
                    <option
                      key={`${item.country}-${item.code}`}
                      value={item.code}
                    >
                      {item.code} · {item.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>{t("Teléfono")}</span>
                <input
                  className={inputClass("telefono")}
                  type="tel"
                  value={telefono}
                  onChange={(e) =>
                    setTelefono(cleanPhone(e.target.value, countryCode))
                  }
                  placeholder={selectedCountry.placeholder}
                  autoComplete="tel-national"
                />
                {fieldErrors.telefono ? (
                  <small>{t(fieldErrors.telefono)}</small>
                ) : null}
              </label>
              <label className="wide">
                <span>{t("Fecha de nacimiento")}</span>
                <input
                  className={inputClass("fechaNacimiento")}
                  type="date"
                  value={fechaNacimiento}
                  onChange={(e) => setFechaNacimiento(e.target.value)}
                  autoComplete="bday"
                />
                {fieldErrors.fechaNacimiento ? (
                  <small>{t(fieldErrors.fechaNacimiento)}</small>
                ) : null}
              </label>
              <label>
                <span>{t("Contraseña")}</span>
                <div className="register-password-field">
                  <input
                    id="register-password"
                    className={inputClass("password")}
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("Mínimo 6 caracteres")}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="register-password-toggle"
                    onClick={() => setShowPassword((visible) => !visible)}
                    aria-label={showPassword ? t("Ocultar contraseña") : t("Mostrar contraseña")}
                    aria-pressed={showPassword}
                    aria-controls="register-password"
                    title={showPassword ? t("Ocultar contraseña") : t("Mostrar contraseña")}
                  >
                    {showPassword ? (
                      <EyeOff size={20} strokeWidth={2.2} aria-hidden="true" />
                    ) : (
                      <Eye size={20} strokeWidth={2.2} aria-hidden="true" />
                    )}
                  </button>
                </div>
                {fieldErrors.password ? (
                  <small>{t(fieldErrors.password)}</small>
                ) : null}
              </label>
              <label>
                <span>{t("Confirmar contraseña")}</span>
                <div className="register-password-field">
                  <input
                    id="register-password-confirm"
                    className={inputClass("confirm")}
                    type={showConfirm ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder={t("Repite tu contraseña")}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="register-password-toggle"
                    onClick={() => setShowConfirm((visible) => !visible)}
                    aria-label={showConfirm ? t("Ocultar confirmación de contraseña") : t("Mostrar confirmación de contraseña")}
                    aria-pressed={showConfirm}
                    aria-controls="register-password-confirm"
                    title={showConfirm ? t("Ocultar contraseña") : t("Mostrar contraseña")}
                  >
                    {showConfirm ? (
                      <EyeOff size={20} strokeWidth={2.2} aria-hidden="true" />
                    ) : (
                      <Eye size={20} strokeWidth={2.2} aria-hidden="true" />
                    )}
                  </button>
                </div>
                {fieldErrors.confirm ? (
                  <small>{t(fieldErrors.confirm)}</small>
                ) : null}
              </label>
            </div>

            {password ? (
              <div className="register-pro-strength">
                <div>
                  <span
                    style={{
                      width: `${Math.min(100, Math.max(20, passwordStrength.score * 20))}%`,
                    }}
                  />
                </div>
                <strong>{t("Seguridad:") + " "}{passwordStrength.label}</strong>
              </div>
            ) : null}

            <label
              className={`register-pro-terms ${fieldErrors.acceptedTerms ? "is-error" : ""}`}
            >
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
              />
              <span>{t("Confirmo que soy mayor de edad y acepto los")}{" "}
                <Link to="/terms" target="_blank" rel="noreferrer">{t("Términos y condiciones")}</Link>{t(", la")}{" "}
                <Link to="/privacy" target="_blank" rel="noreferrer">{t("Política de privacidad")}</Link>{" "}{t("y las")}{" "}
                <Link to="/payments" target="_blank" rel="noreferrer">{t("condiciones de pagos, cancelaciones y reembolsos")}</Link>
                .
              </span>
            </label>
            {fieldErrors.acceptedTerms ? (
              <small className="register-pro-terms-error">
                {t(fieldErrors.acceptedTerms)}
              </small>
            ) : null}

            <button
              type="submit"
              className="register-pro-submit"
              disabled={busy}
            >
              {loading ? "Creando cuenta..." : "Crear cuenta con correo"}
            </button>

            <p className="register-pro-footnote">{t("Te enviaremos un código de verificación al correo indicado.")}</p>
          </form>
        )}

        <p className="register-pro-login-link">{t("¿Ya tienes cuenta?")}{" "}
          <button
            type="button"
            onClick={() =>
              navigate(
                nextTarget
                  ? `/login?from=${encodeURIComponent(nextTarget)}`
                  : "/login",
                { state: { from: nextTarget || "/" } },
              )
            }
          >{t("Inicia sesión")}</button>
        </p>
      </section>
    </main>
  );
}

const styles = `
  .register-pro-page{min-height:calc(100dvh - 86px);display:grid;place-items:center;padding:28px 16px calc(34px + env(safe-area-inset-bottom,0px));background:radial-gradient(circle at 18% 0%,rgba(0,103,95,.10),transparent 30%),linear-gradient(180deg,#f8fafc 0%,#fff 56%,#f8fafc 100%);color:#0f172a;box-sizing:border-box}.register-pro-card{width:min(100%,580px);border:1px solid rgba(226,232,240,.96);border-radius:32px;background:rgba(255,255,255,.98);box-shadow:0 28px 80px rgba(15,23,42,.12);padding:24px;box-sizing:border-box}.register-pro-top{display:flex;gap:16px;align-items:center;margin-bottom:20px}.register-pro-logo{width:122px;height:58px;display:grid;place-items:center;overflow:hidden;flex:0 0 auto}.register-pro-logo img{width:100%;height:100%;display:block;object-fit:cover;object-position:center;user-select:none;-webkit-user-drag:none}.register-pro-eyebrow{color:${BRAND};font-size:11px;font-weight:950;letter-spacing:.13em;text-transform:uppercase;margin-bottom:6px}.register-pro-card h1{margin:0;font-size:clamp(32px,6vw,46px);line-height:.98;letter-spacing:-.065em;font-weight:950;color:#0f172a}.register-pro-card p{margin:9px 0 0;color:#64748b;font-size:14px;line-height:1.45;font-weight:720}.register-pro-alert{border:1px solid #fecaca;background:#fef2f2;color:#b91c1c;border-radius:20px;padding:13px 14px;font-size:13px;font-weight:850;margin-bottom:16px;display:flex;align-items:center;justify-content:space-between;gap:12px}.register-pro-alert button{border:0;background:#fff;color:#b91c1c;border-radius:999px;padding:8px 10px;font-size:12px;font-weight:950;cursor:pointer;white-space:nowrap}.register-fast-options{display:grid;gap:12px}.register-social,.register-email-choice{width:100%;min-height:54px;border-radius:18px;display:flex;align-items:center;justify-content:center;gap:12px;font-size:15px;font-weight:950;cursor:pointer;transition:transform 150ms ease,background 150ms ease}.register-social:active,.register-email-choice:active{transform:scale(.99)}.register-social:disabled,.register-email-choice:disabled{opacity:.58;cursor:not-allowed}.register-social.google{border:1px solid #dbe2ea;background:#fff;color:#111827}.register-social.google:hover{background:#f8fafc}.register-social.apple{border:1px solid #020617;background:#020617;color:#fff}.register-social.apple:hover{background:#000}.register-email-choice{border:1px solid rgba(0,103,95,.22);background:#eefaf8;color:${BRAND}}.register-divider{display:flex;align-items:center;gap:12px;color:#94a3b8;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.14em}.register-divider:before,.register-divider:after{content:"";height:1px;background:#e2e8f0;flex:1}.register-social-note{text-align:center;font-size:12px!important;margin-top:2px!important}.register-legal-note{text-align:center;font-size:11px!important;margin-top:0!important}.register-legal-note a{color:${BRAND};font-weight:950;text-decoration:none}.register-back-options{border:0;background:transparent;color:${BRAND};font-size:13px;font-weight:950;padding:0;margin:0 0 15px;cursor:pointer}.register-pro-grid{display:grid;grid-template-columns:1fr 1fr;gap:13px}.register-pro-grid label{min-width:0}.register-pro-grid label.wide{grid-column:1/-1}.register-pro-grid label>span{display:block;color:#334155;font-size:13px;font-weight:900;margin-bottom:7px}.register-pro-input{width:100%;min-height:52px;border-radius:18px;border:1px solid #e2e8f0;background:#fff;color:#0f172a;outline:none;font-size:15px;font-weight:760;padding:0 14px;box-sizing:border-box;transition:border-color 150ms ease,box-shadow 150ms ease}.register-pro-input:focus{border-color:rgba(0,103,95,.42);box-shadow:0 0 0 4px rgba(0,103,95,.10)}.register-password-field{position:relative}.register-password-field .register-pro-input{padding-right:54px}.register-password-toggle{position:absolute;right:5px;top:50%;transform:translateY(-50%);width:42px;height:42px;border:0;border-radius:14px;background:transparent;color:#64748b;display:grid;place-items:center;cursor:pointer;transition:background 150ms ease,color 150ms ease,box-shadow 150ms ease}.register-password-toggle:hover{background:#f1f5f9;color:${BRAND}}.register-password-toggle:focus-visible{outline:none;box-shadow:0 0 0 3px rgba(0,103,95,.16);color:${BRAND}}.register-pro-input.is-error{border-color:#fecaca;background:#fff7f7}.register-pro-grid small,.register-pro-terms-error{display:block;margin-top:6px;color:#dc2626;font-size:12px;font-weight:800}.register-pro-strength{display:flex;align-items:center;gap:10px;margin-top:14px;color:#64748b;font-size:12px;font-weight:850}.register-pro-strength>div{height:8px;flex:1;border-radius:999px;background:#e2e8f0;overflow:hidden}.register-pro-strength span{display:block;height:100%;border-radius:inherit;background:linear-gradient(135deg,${BRAND},${BRAND_2})}.register-pro-terms{margin-top:15px;display:flex;gap:11px;align-items:flex-start;border:1px solid #e2e8f0;background:#f8fafc;border-radius:20px;padding:13px;color:#475569;font-size:13px;line-height:1.4;font-weight:800}.register-pro-terms.is-error{border-color:#fecaca;background:#fff7f7}.register-pro-terms a{color:${BRAND};font-weight:950;text-decoration:none}.register-pro-terms input{width:18px;height:18px;accent-color:${BRAND};margin-top:1px;flex:0 0 auto}.register-pro-submit{width:100%;min-height:54px;margin-top:16px;border:0;border-radius:20px;background:linear-gradient(135deg,${BRAND},${BRAND_2});color:#fff;font-size:15px;font-weight:950;box-shadow:0 16px 34px rgba(0,103,95,.23);cursor:pointer}.register-pro-submit:disabled{opacity:.62;cursor:not-allowed}.register-pro-login-link{text-align:center;font-size:13px!important;margin-top:18px!important;font-weight:800!important}.register-pro-login-link button{border:0;background:transparent;color:${BRAND};font-weight:950;cursor:pointer;padding:0}.register-pro-footnote{text-align:center;font-size:12px!important;line-height:1.45!important;margin-top:12px!important}@media(max-width:560px){.register-pro-page{min-height:100dvh;padding:12px 10px calc(24px + env(safe-area-inset-bottom,0px));place-items:start center}.register-pro-card{padding:20px;border-radius:28px}.register-pro-top{gap:12px;align-items:center}.register-pro-logo{width:102px;height:50px}.register-pro-grid{grid-template-columns:1fr}.register-pro-grid label.wide{grid-column:auto}.register-pro-alert{align-items:flex-start;flex-direction:column}}
`;
